/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: chen
 *  创建时间: 2026-06-26 13:08
 *  文件名: src/hooks/foundations.ts
 *  功能描述:
 */

import AxiosWrap from '../lib/request';
import { getStudySectionId } from '../util';

type FoundationType = 'grades' | 'subjects';
type FoundationCache = {
  [studySectionCode: string]: Partial<Record<FoundationType, any[]>>;
};

const FOUNDATION_CACHE_KEY = 'foundationCache';
let foundationCache: FoundationCache | null = null;
const pendingRequests: { [key: string]: Promise<any[]> | undefined } = {};

function getCacheKey(studySectionCode?: string) {
  return studySectionCode || '';
}

function readCache(): FoundationCache {
  if (foundationCache) {
    return foundationCache;
  }

  try {
    const cache = window.localStorage.getItem(FOUNDATION_CACHE_KEY);
    foundationCache = cache ? JSON.parse(cache) : {};
  } catch (error) {
    foundationCache = {};
  }

  return foundationCache || {};
}

function writeCache(cache: FoundationCache) {
  foundationCache = cache;
  try {
    window.localStorage.setItem(FOUNDATION_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // localStorage 写入失败时保留内存缓存，避免影响正常业务流程
  }
}

function getCacheData(type: FoundationType, studySectionCode?: string) {
  const cache = readCache();
  const data = cache[getCacheKey(studySectionCode)]?.[type];
  return Array.isArray(data) ? data : null;
}

function setCacheData(type: FoundationType, studySectionCode: string | undefined, data: any[]) {
  const cache = readCache();
  const cacheKey = getCacheKey(studySectionCode);
  writeCache({
    ...cache,
    [cacheKey]: {
      ...(cache[cacheKey] || {}),
      [type]: data,
    },
  });
}

async function getFoundationData(
  type: FoundationType,
  studySectionCode?: string,
  options?: { ignoreLoading?: boolean },
) {
  const cachedData = getCacheData(type, studySectionCode);
  if (cachedData) {
    return cachedData;
  }

  const pendingKey = `${type}:${getCacheKey(studySectionCode)}`;
  if (pendingRequests[pendingKey]) {
    return pendingRequests[pendingKey] as Promise<any[]>;
  }

  pendingRequests[pendingKey] = (AxiosWrap as any)({
    url: `/foundations/${type}`,
    params: { studySectionCode },
    ignoreLoading: options?.ignoreLoading,
  })
    .then(({ data }: { data: any }) => {
      const list = Array.isArray(data) ? data : [];
      setCacheData(type, studySectionCode, list);
      return list;
    })
    .finally(() => {
      delete pendingRequests[pendingKey];
    });

  return pendingRequests[pendingKey] as Promise<any[]>;
}

export function getFoundationGrades(
  studySectionCode?: string,
  options?: { ignoreLoading?: boolean },
) {
  return getFoundationData('grades', studySectionCode, options);
}

export function getFoundationSubjects(
  studySectionCode?: string,
  options?: { ignoreLoading?: boolean },
) {
  return getFoundationData('subjects', studySectionCode, options);
}

export async function preloadFoundationData(studySectionCode = getStudySectionId()) {
  await Promise.all([
    getFoundationGrades(studySectionCode, { ignoreLoading: true }).catch(() => []),
    getFoundationSubjects(studySectionCode, { ignoreLoading: true }).catch(() => []),
  ]);
}
